import { defaultCardDesign, type PassportCard } from '../../services/api/passportCards';
import type { PassportKind } from '@bilt/domain/passportRegistry';
import type { PassportSubjectPreview } from '../../services/api/passport';
import { normalizedFocalValue, normalizedPhotoZoom } from '../../constants/imageCropPresets';

export type SourceCardInput = {
  kind: PassportKind;
  id: number;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  image?: string | null;
  logo?: string | null;
  facts?: PassportSubjectPreview['facts'];
  slug?: string | null;
  owner?: string;
  private?: boolean;
};
/** Presentation only. Never copy issuance/ownership/supply from an ordinary entity payload. */
export function sourceCard(input: SourceCardInput): PassportCard {
  return {
    id: null,
    key: `subject:${input.kind}:${input.id}`,
    subject_type: input.kind,
    subject_id: input.id,
    title: input.title,
    issuer: 'Bilt:Spec',
    status: input.private ? 'private' : 'preview',
    rules: { evidence: 'qr', cap: null },
    design: {
      ...defaultCardDesign,
      template:
        input.kind === 'vehicle'
          ? 'build'
          : input.kind === 'event' || input.kind === 'club'
            ? input.kind
            : 'place',
      story: input.description || '',
    },
    subject: {
      subject_type: input.kind,
      subject_id: input.id,
      title: input.title,
      subtitle: input.subtitle || null,
      public_slug: input.slug || null,
      owner_username: input.owner,
      image_url: input.image || input.logo || null,
      facts: input.facts || {},
      collectible: {
        kind: input.kind,
        cover_url: input.image || null,
        logo_url: input.logo || null,
      },
    },
  };
}
export interface VehicleSource {
  card_projection?: {
    modification_systems?: NonNullable<PassportSubjectPreview['card_summary']>['modification_systems'];
    template_version?: string;
    facts: NonNullable<PassportSubjectPreview['facts']>;
    badges?: import('../../types/discoveryBadges').IssuedBadge[];
    badge_overflow?: number;
    limited_designation?: NonNullable<
      PassportSubjectPreview['card_summary']
    >['limited_designation'];
  };
  id: number;
  year?: number;
  make?: string;
  model?: string;
  trim?: string;
  display_name?: string;
  nickname?: string | null;
  description?: string | null;
  owner_username?: string;
  image_url?: string | null;
  focal_x?: number | string | null;
  focal_y?: number | string | null;
  zoom?: number | string | null;
  public_slug?: string | null;
  slug?: string | null;
  is_public?: boolean;
  engine?: string | null;
  chassis?: string | null;
  drivetrain?: string | null;
  horsepower?: number;
  horsepower_measurement?: 'wheel' | 'crank' | null;
  torque?: number;
  redline?: number;
  modification_count?: number;
  total_investment?: number;
  color?: string;
  exterior_finish_label?: string | null;
  certification_status?: string | null;
  created_at?: string;
  updated_at?: string;
  view_count?: number;
  like_count?: number;
  favorite_count?: number;
  user_has_liked?: boolean;
  is_favorited?: boolean;
  transmission?: string | null;
  base_rarity_level?: string;
  base_collector_points?: number;
  settings?: { show_performance?: boolean; show_pricing?: boolean; show_modifications?: boolean };
}
export function vehicleSourceCard(vehicle: VehicleSource, isOwner = false): PassportCard {
  const modifications = isOwner || vehicle.settings?.show_modifications !== false;
  const performance = isOwner || vehicle.settings?.show_performance !== false;
  const card = sourceCard({
    kind: 'vehicle',
    id: vehicle.id,
    title:
      vehicle.display_name ||
      vehicle.nickname ||
      [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') ||
      'Vehicle build',
    subtitle: [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(' '),
    description: vehicle.description,
    image: vehicle.image_url,
    owner: vehicle.owner_username,
    slug: vehicle.public_slug || vehicle.slug,
    private: vehicle.is_public === false,
    facts: {
      year: vehicle.year ?? null,
      make: vehicle.make || null,
      model: vehicle.model || null,
      engine: vehicle.engine || null,
      chassis: vehicle.chassis || null,
      drivetrain: vehicle.drivetrain || null,
      transmission: vehicle.transmission || null,
      finish: vehicle.exterior_finish_label || vehicle.color || null,
      certification_status: vehicle.certification_status || null,
      cost:
        modifications && (isOwner || vehicle.settings?.show_pricing !== false)
          ? (vehicle.total_investment ?? null)
          : null,
      updated_at: vehicle.updated_at || vehicle.created_at || null,
      view_count: vehicle.view_count ?? null,
      like_count: vehicle.like_count ?? null,
      favorite_count: vehicle.favorite_count ?? null,
      user_has_liked: vehicle.user_has_liked == null ? vehicle.card_projection?.facts.user_has_liked ?? null : Number(vehicle.user_has_liked),
      is_favorited: vehicle.is_favorited == null ? vehicle.card_projection?.facts.is_favorited ?? null : Number(vehicle.is_favorited),
      base_rarity_level: vehicle.base_rarity_level || null,
      base_collector_points: vehicle.base_collector_points ?? null,
      builder: vehicle.owner_username || null,
      modification_count: modifications ? (vehicle.modification_count ?? null) : null,
      power: performance ? (vehicle.horsepower ?? null) : null,
      power_unit:
        performance && vehicle.horsepower
          ? vehicle.horsepower_measurement === 'wheel'
            ? 'WHP'
            : 'HP'
          : null,
      power_measurement: performance ? vehicle.horsepower_measurement || null : null,
      torque: performance ? (vehicle.torque ?? null) : null,
      redline: performance ? (vehicle.redline ?? null) : null,
    },
  });
  card.preview_context = isOwner ? 'owner' : 'public';
  if (card.subject) {
    if (vehicle.card_projection?.template_version === 'bilt.collector.v2') {
      card.subject.facts = {
        ...vehicle.card_projection.facts,
        user_has_liked: vehicle.user_has_liked == null ? vehicle.card_projection?.facts.user_has_liked ?? null : Number(vehicle.user_has_liked),
        is_favorited: vehicle.is_favorited == null ? vehicle.card_projection?.facts.is_favorited ?? null : Number(vehicle.is_favorited),
        view_count: vehicle.view_count ?? vehicle.card_projection.facts.view_count ?? null,
        like_count: vehicle.like_count ?? vehicle.card_projection.facts.like_count ?? null,
        favorite_count:
          vehicle.favorite_count ?? vehicle.card_projection.facts.favorite_count ?? null,
      };
      card.subject.card_summary = {
        modification_systems: vehicle.card_projection.modification_systems,
        badges: vehicle.card_projection.badges,
        badge_overflow: vehicle.card_projection.badge_overflow,
        limited_designation: vehicle.card_projection.limited_designation,
      };
    }
    if (!isOwner && vehicle.card_projection?.facts.images_public === 0) {
      card.subject.image_url = null;
      if (card.subject.collectible) card.subject.collectible.cover_url = null;
    }
    card.subject.focal_x = normalizedFocalValue(vehicle.focal_x);
    card.subject.focal_y = normalizedFocalValue(vehicle.focal_y);
    card.subject.zoom = normalizedPhotoZoom(vehicle.zoom);
  }
  return card;
}

/** A journal encounter never manufactures base ownership, an edition, or an ordinal. */
export function encounterCard(
  encounter: import('../../services/api/passport').PassportEncounter
): PassportCard {
  return {
    id: null,
    key: `encounter:${encounter.public_id}`,
    title: encounter.subject?.title || 'Collected record',
    issuer: 'Bilt:Spec',
    subject_type: encounter.subject_type,
    subject_id: encounter.subject_id,
    status: encounter.status,
    encounter_id: encounter.public_id,
    evidence: encounter.method,
    design: {
      ...defaultCardDesign,
      template:
        encounter.subject_type === 'vehicle'
          ? 'build'
          : encounter.subject_type === 'event' || encounter.subject_type === 'club'
            ? encounter.subject_type
            : 'place',
    },
    rules: { evidence: 'qr', cap: null },
    subject: encounter.subject || null,
  };
}
